// Tax Notification Dashboard
// Purpose: Admin UI for managing and monitoring tax notifications
// Features: View notifications, trigger simulations, monitor system health

import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Card,
  Grid,
  Text,
  Badge,
  Button,
  Table,
  Group,
  Stack,
  Tabs,
  Alert,
  Loader,
  ActionIcon,
  Modal,
  TextInput,
  NumberInput,
  Select,
  Timeline,
  ThemeIcon,
  Progress,
  Code,
  JsonInput, Notification,} from '@mantine/core';
import {
  IconBell,
  IconCheck,
  IconClock,
  IconAlertTriangle,
  IconRefresh,
  IconRocket,
  IconDatabase,
  IconServer,
  IconMail,
  IconBrandSlack,
  IconPhone,
  IconChartBar,
  IconSettings,
} from '@tabler/icons-react';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../security/useAuth';

// GraphQL Queries
const GET_NOTIFICATIONS = gql`
  query GetNotifications($input: GetNotificationsInput!) {
    getNotifications(input: $input) {
      notifications {
        pk
        sk
        companyId
        notificationType
        sendDate
        status
        jurisdiction
        year
        category
        effectiveDate
        impactSummary {
          affectedEmployeeCount
          averageImpactPerPaycheck
          totalMonthlyImpact
        }
        channels
        createdAt
        sentAt
      }
      totalCount
      nextToken
    }
  }
`;

const GET_NOTIFICATION_STATS = gql`
  query GetNotificationStats($companyId: String!, $tenantId: String!) {
    getNotificationStats(companyId: $companyId, tenantId: $tenantId) {
      scheduledCount
      sentCount
      failedCount
      nextNotificationDate
      lastSentDate
    }
  }
`;

const SEND_TEST_NOTIFICATION = gql`
  mutation SendTestNotification($input: SendTestNotificationInput!) {
    sendTestNotification(input: $input) {
      success
      message
    }
  }
`;

const TRIGGER_TAX_CHANGE = gql`
  mutation TriggerTaxChange($input: TriggerTaxChangeInput!) {
    triggerTaxChange(input: $input) {
      success
      message
      notificationIds
    }
  }
`;

// Types
interface Notification {
  pk: string;
  sk: string;
  companyId: string;
  notificationType: string;
  sendDate: string;
  status: string;
  jurisdiction: string;
  year: number;
  category: string;
  effectiveDate: string;
  impactSummary: {
    affectedEmployeeCount: number;
    averageImpactPerPaycheck: number;
    totalMonthlyImpact: number;
  };
  channels: string[];
  createdAt: string;
  sentAt?: string;
}

interface NotificationStats {
  scheduledCount: number;
  sentCount: number;
  failedCount: number;
  nextNotificationDate?: string;
  lastSentDate?: string;
}

export function TaxNotificationDashboard() {
  // =========================================================================
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS (Rules of Hooks)
  // =========================================================================

  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [simulationModalOpen, setSimulationModalOpen] = useState(false);
  const [systemHealthModalOpen, setSystemHealthModalOpen] = useState(false);

  // Get tenant context from auth (FERP pattern: no hardcoded values)
  const { tenant } = useAuth();
  const tenantId = tenant?.id;
  const companyId = tenant?.id; // Company ID is same as tenant ID in multi-tenant architecture

  // Queries with error handling (skip if no tenant)
  const { data: notificationsData, loading: notificationsLoading, refetch: refetchNotifications } = useQuery<Record<string, any>>(
    GET_NOTIFICATIONS,
    {
      variables: {
        input: {
          companyId: companyId || '',
          tenantId: tenantId || '',
        },
      },
      errorPolicy: 'all',
      skip: !tenantId, // Skip query if no tenant
      pollInterval: 5000, // Refresh every 5 seconds
    }
  );

  const { data: statsData, loading: statsLoading, error: statsError } = useQuery<Record<string, any>>(GET_NOTIFICATION_STATS, {
    variables: { companyId: companyId || '', tenantId: tenantId || '' },
    pollInterval: 5000,
    errorPolicy: 'all',
    skip: !tenantId, // Skip query if no tenant
  });

  // Mutations
  const [sendTestNotification, { loading: sendingTest }] = useMutation<Record<string, any>>(SEND_TEST_NOTIFICATION);

  // =========================================================================
  // CONDITIONAL RETURNS (after all hooks are called)
  // =========================================================================

  // Require tenant context
  if (!tenantId) {
    return (
      <Container>
        <Alert color="red" icon={<IconAlertTriangle />}>
          Tenant context required. Please ensure you are authenticated.
        </Alert>
      </Container>
    );
  }

  // Use mock data if GraphQL backend not available
  const notifications: Notification[] = notificationsData?.getNotifications?.notifications || [];
  const stats: NotificationStats | undefined = statsData?.getNotificationStats || (statsError ? {
    scheduledCount: 0,
    sentCount: 0,
    failedCount: 0,
  } : undefined);

  // Notification type colors
  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case 'tax_change_advance_notice':
        return 'blue';
      case 'tax_change_reminder':
        return 'yellow';
      case 'tax_change_effective':
        return 'green';
      case 'tax_change_impact_report':
        return 'violet';
      default:
        return 'gray';
    }
  };

  // Status colors
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'blue';
      case 'sent':
        return 'green';
      case 'failed':
        return 'red';
      case 'cancelled':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Format notification type
  const formatNotificationType = (type: string) => {
    return type
      .replace('tax_change_', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={1}>Tax Notification System</Title>
            <Text c="dimmed" size="sm">
              Monitor and manage tax law change notifications
            </Text>
          </div>
          <Group>
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="light"
              onClick={() => refetchNotifications()}
            >
              Refresh
            </Button>
            <Button
              leftSection={<IconRocket size={16} />}
              onClick={() => setSimulationModalOpen(true)}
            >
              Run Simulation
            </Button>
            <Button
              leftSection={<IconSettings size={16} />}
              variant="outline"
              onClick={() => setSystemHealthModalOpen(true)}
            >
              System Health
            </Button>
          </Group>
        </Group>

        {/* Stats Cards */}
        {statsLoading ? (
          <Loader />
        ) : stats ? (
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card withBorder>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Scheduled
                    </Text>
                    <ThemeIcon variant="light" color="blue" size="sm">
                      <IconClock size={16} />
                    </ThemeIcon>
                  </Group>
                  <Title order={2}>{stats.scheduledCount}</Title>
                  {stats.nextNotificationDate && (
                    <Text size="xs" c="dimmed">
                      Next: {format(parseISO(stats.nextNotificationDate), 'MMM d, yyyy')}
                    </Text>
                  )}
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card withBorder>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Sent
                    </Text>
                    <ThemeIcon variant="light" color="green" size="sm">
                      <IconCheck size={16} />
                    </ThemeIcon>
                  </Group>
                  <Title order={2}>{stats.sentCount}</Title>
                  {stats.lastSentDate && (
                    <Text size="xs" c="dimmed">
                      Last: {format(parseISO(stats.lastSentDate), 'MMM d, yyyy')}
                    </Text>
                  )}
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card withBorder>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Failed
                    </Text>
                    <ThemeIcon variant="light" color="red" size="sm">
                      <IconAlertTriangle size={16} />
                    </ThemeIcon>
                  </Group>
                  <Title order={2}>{stats.failedCount}</Title>
                  {stats.failedCount > 0 && (
                    <Text size="xs" c="red">
                      Requires attention
                    </Text>
                  )}
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card withBorder>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      Total
                    </Text>
                    <ThemeIcon variant="light" color="violet" size="sm">
                      <IconBell size={16} />
                    </ThemeIcon>
                  </Group>
                  <Title order={2}>
                    {stats.scheduledCount + stats.sentCount + stats.failedCount}
                  </Title>
                  <Text size="xs" c="dimmed">
                    All notifications
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
        ) : null}

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconBell size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="notifications" leftSection={<IconMail size={16} />}>
              Notifications
            </Tabs.Tab>
            <Tabs.Tab value="impact" leftSection={<IconChartBar size={16} />}>
              Impact Analysis
            </Tabs.Tab>
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="md">
            <Stack gap="md">
              <Alert
                icon={<IconBell size={16} />}
                title="Tax Notification System Active"
                color="blue"
              >
                Monitoring tax law changes daily. Notifications are sent 30 days, 7 days, day-of,
                and post-payroll.
              </Alert>

              {notifications.length > 0 && (
                <Card withBorder>
                  <Stack gap="md">
                    <Title order={3}>Recent Notifications</Title>
                    <Timeline active={-1} lineWidth={2}>
                      {notifications.slice(0, 5).map((notification) => (
                        <Timeline.Item
                          key={notification.pk + notification.sk}
                          bullet={
                            notification.status === 'sent' ? (
                              <IconCheck size={12} />
                            ) : notification.status === 'failed' ? (
                              <IconAlertTriangle size={12} />
                            ) : (
                              <IconClock size={12} />
                            )
                          }
                          title={formatNotificationType(notification.notificationType)}
                          color={getStatusColor(notification.status)}
                        >
                          <Text size="sm" c="dimmed">
                            {notification.status === 'sent'
                              ? `Sent on ${format(parseISO(notification.sentAt!), 'MMM d, yyyy')}`
                              : `Scheduled for ${format(
                                  parseISO(notification.sendDate),
                                  'MMM d, yyyy'
                                )}`}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {notification.impactSummary.affectedEmployeeCount} employees affected
                            ({notification.impactSummary.averageImpactPerPaycheck >= 0 ? '+' : ''}$
                            {notification.impactSummary.averageImpactPerPaycheck.toFixed(2)}/
                            paycheck)
                          </Text>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </Stack>
                </Card>
              )}
            </Stack>
          </Tabs.Panel>

          {/* Notifications Tab */}
          <Tabs.Panel value="notifications" pt="md">
            {notificationsLoading ? (
              <Loader />
            ) : notifications.length === 0 ? (
              <Alert icon={<IconBell size={16} />} title="No Notifications" color="blue">
                No tax notifications scheduled. Trigger a simulation to test the system.
              </Alert>
            ) : (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Send Date</Table.Th>
                    <Table.Th>Tax Change</Table.Th>
                    <Table.Th>Impact</Table.Th>
                    <Table.Th>Channels</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {notifications.map((notification) => (
                    <Table.Tr key={notification.pk + notification.sk}>
                      <Table.Td>
                        <Badge color={getNotificationTypeColor(notification.notificationType)}>
                          {formatNotificationType(notification.notificationType)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={getStatusColor(notification.status)}>
                          {notification.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {format(parseISO(notification.sendDate), 'MMM d, yyyy')}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {notification.jurisdiction} {notification.year}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {notification.category.replace(/_/g, ' ')}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {notification.impactSummary.affectedEmployeeCount} employees
                        </Text>
                        <Text size="xs" c="dimmed">
                          ${Math.abs(notification.impactSummary.averageImpactPerPaycheck).toFixed(
                            2
                          )}
                          /paycheck
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          {notification.channels.includes('email') && (
                            <ThemeIcon size="sm" variant="light">
                              <IconMail size={12} />
                            </ThemeIcon>
                          )}
                          {notification.channels.includes('slack') && (
                            <ThemeIcon size="sm" variant="light">
                              <IconBrandSlack size={12} />
                            </ThemeIcon>
                          )}
                          {notification.channels.includes('sms') && (
                            <ThemeIcon size="sm" variant="light">
                              <IconPhone size={12} />
                            </ThemeIcon>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="light"
                          onClick={() => setSelectedNotification(notification)}
                        >
                          View Details
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Tabs.Panel>

          {/* Impact Analysis Tab */}
          <Tabs.Panel value="impact" pt="md">
            <Stack gap="md">
              <Card withBorder>
                <Stack gap="md">
                  <Title order={3}>Impact Summary</Title>
                  {notifications.length > 0 ? (
                    <div>
                      <Text size="sm" fw={500}>
                        Total Employees Affected:{' '}
                        {notifications.reduce(
                          (sum, n) => sum + n.impactSummary.affectedEmployeeCount,
                          0
                        )}
                      </Text>
                      <Text size="sm">
                        Average Impact per Paycheck: $
                        {(
                          notifications.reduce(
                            (sum, n) => sum + n.impactSummary.averageImpactPerPaycheck,
                            0
                          ) / notifications.length
                        ).toFixed(2)}
                      </Text>
                      <Text size="sm">
                        Total Monthly Impact: $
                        {notifications
                          .reduce((sum, n) => sum + n.impactSummary.totalMonthlyImpact, 0)
                          .toFixed(2)}
                      </Text>
                    </div>
                  ) : (
                    <Text c="dimmed">No impact data available</Text>
                  )}
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* Simulation Modal */}
      <SimulationModal
        opened={simulationModalOpen}
        onClose={() => setSimulationModalOpen(false)}
        onSimulationComplete={() => {
          refetchNotifications();
          setSimulationModalOpen(false);
        }}
      />

      {/* System Health Modal */}
      <SystemHealthModal
        opened={systemHealthModalOpen}
        onClose={() => setSystemHealthModalOpen(false)}
      />

      {/* Notification Details Modal */}
      <NotificationDetailsModal
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
      />
    </Container>
  );
}

// Simulation Modal Component
function SimulationModal({
  opened,
  onClose,
  onSimulationComplete,
}: {
  opened: boolean;
  onClose: () => void;
  onSimulationComplete: () => void;
}) {
  const [simulating, setSimulating] = useState(false);
  const [simulationResult, setSimulationResult] = useState<string | null>(null);
  const [triggerTaxChange] = useMutation<Record<string, any>>(TRIGGER_TAX_CHANGE);

  const handleSimulation = async () => {
    setSimulating(true);
    try {
      // Trigger tax change via GraphQL mutation
      const effectiveDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const { data } = await triggerTaxChange({
        variables: {
          input: {
            companyId: 'acme-corp',
            tenantId: 'test-tenant',
            jurisdiction: 'US',
            year: 2026,
            taxType: 'SOCIAL_SECURITY',
            newWageBase: 176400,
            effectiveDate,
          },
        },
      });

      if (data?.triggerTaxChange?.success) {
        setSimulationResult(
          `✓ Success!\n\nMessage: ${data?.triggerTaxChange.message}\n\nNotifications Created:\n${data?.triggerTaxChange.notificationIds.join('\n')}`
        );
        setTimeout(() => {
          onSimulationComplete();
        }, 2000);
      } else {
        setSimulationResult('Failed to trigger tax change');
      }
    } catch (error) {
      setSimulationResult(`Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setSimulating(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Tax Law Change Simulation" size="lg">
      <Stack gap="md">
        <Alert icon={<IconRocket size={16} />} color="blue">
          This will simulate a tax law change and trigger the notification system.
        </Alert>

        {simulationResult ? (
          <Code block>{simulationResult}</Code>
        ) : (
          <Button
            loading={simulating}
            onClick={handleSimulation}
            leftSection={<IconRocket size={16} />}
            fullWidth
          >
            {simulating ? 'Running Simulation...' : 'Run Tax Change Simulation'}
          </Button>
        )}

        {simulationResult && (
          <Text size="sm" c="green">
            ✓ Simulation complete! Check the Notifications tab.
          </Text>
        )}
      </Stack>
    </Modal>
  );
}

// System Health Modal Component
function SystemHealthModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const [health, setHealth] = useState({
    localstack: false,
    valkey: false,
    lambda: false,
  });
  const [checking, setChecking] = useState(false);

  const checkHealth = async () => {
    setChecking(true);
    try {
      // Check LocalStack
      const localstackHealth = await fetch('http://localhost:4566/_localstack/health');
      setHealth((prev) => ({ ...prev, localstack: localstackHealth.ok }));

      // Check Valkey (via proxy endpoint)
      const valkeyHealth = await fetch('http://localhost:4566/valkey-ping');
      setHealth((prev) => ({ ...prev, valkey: valkeyHealth.ok }));

      // Check Lambda runtime
      const lambdaHealth = await fetch('http://localhost:9000/health');
      setHealth((prev) => ({ ...prev, lambda: lambdaHealth.ok }));
    } catch (error) {
      console.error('Health check error:', error);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (opened) {
      checkHealth();
    }
  }, [opened]);

  return (
    <Modal opened={opened} onClose={onClose} title="System Health" size="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Group>
            <ThemeIcon
              color={health.localstack ? 'green' : 'red'}
              variant="light"
              size="lg"
            >
              <IconDatabase size={20} />
            </ThemeIcon>
            <div>
              <Text size="sm" fw={500}>
                LocalStack
              </Text>
              <Text size="xs" c="dimmed">
                AWS Services Emulation
              </Text>
            </div>
          </Group>
          <Badge color={health.localstack ? 'green' : 'red'}>
            {health.localstack ? 'Healthy' : 'Down'}
          </Badge>
        </Group>

        <Group justify="space-between">
          <Group>
            <ThemeIcon color={health.valkey ? 'green' : 'red'} variant="light" size="lg">
              <IconServer size={20} />
            </ThemeIcon>
            <div>
              <Text size="sm" fw={500}>
                Valkey Cache
              </Text>
              <Text size="xs" c="dimmed">
                Tax Table Caching
              </Text>
            </div>
          </Group>
          <Badge color={health.valkey ? 'green' : 'red'}>
            {health.valkey ? 'Healthy' : 'Down'}
          </Badge>
        </Group>

        <Group justify="space-between">
          <Group>
            <ThemeIcon color={health.lambda ? 'green' : 'red'} variant="light" size="lg">
              <IconRocket size={20} />
            </ThemeIcon>
            <div>
              <Text size="sm" fw={500}>
                Lambda Runtime
              </Text>
              <Text size="xs" c="dimmed">
                Notification Handlers
              </Text>
            </div>
          </Group>
          <Badge color={health.lambda ? 'green' : 'red'}>
            {health.lambda ? 'Healthy' : 'Down'}
          </Badge>
        </Group>

        <Button
          leftSection={<IconRefresh size={16} />}
          onClick={checkHealth}
          loading={checking}
          variant="light"
          fullWidth
        >
          Check Again
        </Button>
      </Stack>
    </Modal>
  );
}

// Notification Details Modal Component
function NotificationDetailsModal({
  notification,
  onClose,
}: {
  notification: Notification | null;
  onClose: () => void;
}) {
  if (!notification) return null;

  return (
    <Modal
      opened={!!notification}
      onClose={onClose}
      title="Notification Details"
      size="lg"
    >
      <Stack gap="md">
        <Group>
          <Badge color={getNotificationTypeColor(notification.notificationType)}>
            {formatNotificationType(notification.notificationType)}
          </Badge>
          <Badge color={getStatusColor(notification.status)}>{notification.status}</Badge>
        </Group>

        <div>
          <Text size="sm" fw={500}>
            Tax Change
          </Text>
          <Text size="sm">
            {notification.jurisdiction} {notification.year} - {notification.category.replace(/_/g, ' ')}
          </Text>
          <Text size="xs" c="dimmed">
            Effective: {format(parseISO(notification.effectiveDate), 'MMMM d, yyyy')}
          </Text>
        </div>

        <div>
          <Text size="sm" fw={500}>
            Impact Summary
          </Text>
          <Text size="sm">
            {notification.impactSummary.affectedEmployeeCount} employees affected
          </Text>
          <Text size="sm">
            Average impact: ${notification.impactSummary.averageImpactPerPaycheck.toFixed(2)}/paycheck
          </Text>
          <Text size="sm">
            Total monthly: ${notification.impactSummary.totalMonthlyImpact.toFixed(2)}
          </Text>
        </div>

        <div>
          <Text size="sm" fw={500}>
            Delivery
          </Text>
          <Group gap="xs">
            {notification.channels.map((channel) => (
              <Badge key={channel} variant="outline">
                {channel}
              </Badge>
            ))}
          </Group>
          <Text size="xs" c="dimmed" mt="xs">
            {notification.status === 'sent'
              ? `Sent on ${format(parseISO(notification.sentAt!), 'MMMM d, yyyy h:mm a')}`
              : `Scheduled for ${format(parseISO(notification.sendDate), 'MMMM d, yyyy')}`}
          </Text>
        </div>

        <JsonInput
          label="Raw Data"
          value={JSON.stringify(notification, null, 2)}
          minRows={10}
          maxRows={15}
          readOnly
        />
      </Stack>
    </Modal>
  );
}

// Helper functions (moved from inline to avoid duplication)
function getNotificationTypeColor(type: string) {
  switch (type) {
    case 'tax_change_advance_notice':
      return 'blue';
    case 'tax_change_reminder':
      return 'yellow';
    case 'tax_change_effective':
      return 'green';
    case 'tax_change_impact_report':
      return 'violet';
    default:
      return 'gray';
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'scheduled':
      return 'blue';
    case 'sent':
      return 'green';
    case 'failed':
      return 'red';
    case 'cancelled':
      return 'gray';
    default:
      return 'gray';
  }
}

function formatNotificationType(type: string) {
  return type
    .replace('tax_change_', '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}
